import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Target, Users, Lightbulb, Award, TrendingUp, Heart } from 'lucide-react';
import { Card } from '../components/ui/card';

const FeatureCard = ({ icon: Icon, title, description, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay }}
  >
    <Card className="p-6 h-full hover:shadow-lg transition-shadow">
      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
        <Icon className="w-6 h-6 text-primary" />
      </div>
      <h3 className="text-lg font-semibold text-foreground mb-2">{title}</h3>
      <p className="text-sm text-muted-foreground">{description}</p>
    </Card>
  </motion.div>
);

export default function AboutUs() {
  return (
    <>
      <Helmet>
        <title>About Us - VisualMathLab | Learning Mathematics with Graphs</title>
        <meta name="description" content="Learn about VisualMathLab's mission to make mathematics accessible through interactive visualizations and step-by-step solutions. Learning with graphs made easy." />
        <meta name="keywords" content="about visualmathlab, math education, visual learning, interactive mathematics, learning with graphs" />
        <link rel="canonical" href="https://visualmathlab.online/about" />
        
        <meta property="og:title" content="About VisualMathLab - Learning with Graphs" />
        <meta property="og:description" content="Discover how VisualMathLab makes mathematics accessible through interactive visualizations." />
        <meta property="og:url" content="https://visualmathlab.online/about" />
      </Helmet>

      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-b from-muted/30 to-background py-20">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-3xl mx-auto text-center"
            >
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6">
                About <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">VisualMathLab</span>
              </h1>
              <p className="text-xl text-muted-foreground mb-4">
                Learning with Graphs
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed">
                We believe that mathematics becomes intuitive when you can see it, interact with it, 
                and understand it step by step. Our mission is to make math education accessible, 
                engaging, and effective through powerful visual tools.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <Card className="p-8 h-full bg-gradient-to-br from-primary/5 to-transparent">
                  <Target className="w-12 h-12 text-primary mb-4" />
                  <h2 className="text-2xl font-bold text-foreground mb-4">Our Mission</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    To democratize mathematics education by providing free, interactive calculators 
                    and visual learning tools that transform abstract concepts into tangible understanding. 
                    We're committed to making every student's math journey more intuitive and successful.
                  </p>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <Card className="p-8 h-full bg-gradient-to-br from-accent/5 to-transparent">
                  <Lightbulb className="w-12 h-12 text-accent mb-4" />
                  <h2 className="text-2xl font-bold text-foreground mb-4">Our Vision</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    A world where every learner can visualize, understand, and master mathematics 
                    through interactive experiences. We envision mathematics education that adapts 
                    to individual learning styles and makes complex concepts accessible to all.
                  </p>
                </Card>
              </motion.div>
            </div>
          </div>
        </section>

        {/* What We Offer */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
                What We Offer
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Comprehensive tools designed to support every aspect of your mathematical learning journey
              </p>
            </motion.div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              <FeatureCard
                icon={TrendingUp}
                title="Interactive Calculators"
                description="20+ specialized calculators covering trigonometry, algebra, geometry, and calculus with real-time results."
                delay={0.1}
              />
              <FeatureCard
                icon={Target}
                title="Visual Representations"
                description="Dynamic graphs, diagrams, and visual aids that update as you input values, making abstract concepts concrete."
                delay={0.2}
              />
              <FeatureCard
                icon={Award}
                title="Step-by-Step Solutions"
                description="Detailed explanations for every calculation, helping you understand the 'why' behind the 'how'."
                delay={0.3}
              />
              <FeatureCard
                icon={Users}
                title="Free for Everyone"
                description="No subscriptions, no paywalls. Quality math education should be accessible to all students and educators."
                delay={0.4}
              />
              <FeatureCard
                icon={Lightbulb}
                title="Learn by Doing"
                description="Experiment with different values and immediately see how they affect results and visualizations."
                delay={0.5}
              />
              <FeatureCard
                icon={Heart}
                title="Built for Students"
                description="Designed with input from educators and students to address real learning challenges and needs."
                delay={0.6}
              />
            </div>
          </div>
        </section>

        {/* Why Visual Learning */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center mb-12"
              >
                <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
                  Why Learning with Graphs?
                </h2>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="space-y-6"
              >
                <Card className="p-6">
                  <h3 className="text-xl font-semibold text-foreground mb-3">Visual Understanding</h3>
                  <p className="text-muted-foreground">
                    Research shows that visual representations significantly improve comprehension and retention. 
                    When you can see mathematical relationships graphically, abstract formulas become intuitive patterns.
                  </p>
                </Card>

                <Card className="p-6">
                  <h3 className="text-xl font-semibold text-foreground mb-3">Immediate Feedback</h3>
                  <p className="text-muted-foreground">
                    Interactive graphs provide instant feedback as you adjust parameters. This real-time interaction 
                    helps develop mathematical intuition and understanding of how variables relate to each other.
                  </p>
                </Card>

                <Card className="p-6">
                  <h3 className="text-xl font-semibold text-foreground mb-3">Multiple Representations</h3>
                  <p className="text-muted-foreground">
                    By showing the same concept through numbers, equations, and graphs simultaneously, we help 
                    students build connections between different mathematical representations.
                  </p>
                </Card>
              </motion.div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-3xl mx-auto text-center"
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
                Join Our Learning Community
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Start exploring our interactive calculators today and experience the power of visual learning.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <a href="/explore">
                  <button className="px-6 py-3 bg-primary text-primary-foreground rounded-xl font-medium hover:opacity-90 transition-opacity">
                    Explore Calculators
                  </button>
                </a>
                <a href="/contact">
                  <button className="px-6 py-3 border border-border rounded-xl font-medium hover:bg-muted transition-colors">
                    Get in Touch
                  </button>
                </a>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
}